import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Activity, ShieldX, ShieldCheck } from "lucide-react";

const ICON_MAP = {
  user_login: { icon: ShieldCheck, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  user_login_failed: { icon: ShieldX, color: "text-red-600 bg-red-50 border-red-200" },
  user_logout: { icon: Activity, color: "text-muted-foreground bg-muted border-border" },
  force_logout_all: { icon: ShieldX, color: "text-orange-600 bg-orange-50 border-orange-200" },
  password_changed: { icon: ShieldCheck, color: "text-blue-600 bg-blue-50 border-blue-200" },
  session_revoked: { icon: ShieldX, color: "text-orange-600 bg-orange-50 border-orange-200" },
};

export default function LoginHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/auth/login-history", { params: { limit: 200, ...(userId ? { user_id: userId } : {}) } })
      .then((r) => setRows(r.data))
      .catch((err) => toast.error?.(fmtError(err)))
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1100px] mx-auto">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Admin</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Login history.</h1>
        </div>
        <Select value={userId || "__all"} onValueChange={(v) => setUserId(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-11 w-full sm:w-60" data-testid="lh-user-filter"><SelectValue placeholder="All users" /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All users</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}
          </SelectContent>
        </Select>
      </header>

      {loading ? (
        <Skeleton className="h-96 rounded-3xl" />
      ) : (
        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-3 space-y-1.5">
            {rows.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">No login activity for this filter.</p>
            ) : rows.map((r) => {
              const def = ICON_MAP[r.action] || ICON_MAP.user_logout;
              const Icon = def.icon;
              return (
                <div key={r.id} className="rounded-2xl border border-border/60 hover:bg-muted/30 transition p-3 flex items-start gap-3 text-xs lg:grid lg:grid-cols-12 lg:items-center lg:gap-3" data-testid={`lh-row-${r.id}`}>
                  <span className={`inline-grid place-items-center h-8 w-8 rounded-full border shrink-0 lg:col-span-1 ${def.color}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="flex-1 min-w-0 space-y-1 lg:contents">
                    <div className="lg:col-span-3 min-w-0">
                      <p className="font-semibold truncate">{r.user_name || r.user_email || "—"}</p>
                      <p className="text-muted-foreground truncate">{r.user_email}</p>
                    </div>
                    <div className="flex items-center justify-between gap-2 lg:contents">
                      <Badge variant="outline" className="font-mono text-[10px] lg:col-span-3 lg:justify-self-start whitespace-nowrap">{r.action}</Badge>
                      <span className="text-muted-foreground truncate lg:col-span-2 hidden sm:inline">{r.meta?.ip || r.meta?.method || "—"}</span>
                      <span className="text-muted-foreground whitespace-nowrap lg:col-span-3 lg:text-right">{fmtDateTime(r.timestamp)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
